import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Button, Empty, Modal, Input, Select, Table, Tag, Tooltip, Popconfirm, message } from "antd";
import {
  ReloadOutlined,
  PlusOutlined,
  MailOutlined,
  TeamOutlined,
  KeyOutlined,
  UserDeleteOutlined,
  CheckCircleTwoTone,
  ClockCircleOutlined,
} from "@ant-design/icons";

import DashboardLayout from "../../layout/dashboardLayout/DashboardLayout";
import {
  getAdministeredOffices,
  getOfficeUsers,
  createOfficeUser,
  inviteOfficeUser,
  setOfficeUserPassword,
  removeOfficeUser,
} from "./controller";
import "../CRM/crm.css";

const ok = (r) => r && r.status >= 200 && r.status < 300;
const err = (r, fallback) => (r && r.json && r.json.detail) || fallback;

/**
 * Settings → User Management. A practice owner/admin picks one of the offices they
 * administer and manages that office's team: create / invite / reset password /
 * remove-from-office. Every action is authorized per-office by the backend
 * (MasterAccess), so this page can only touch offices the caller administers.
 */
export default function UserManagementPage() {
  const [offices, setOffices] = useState([]);
  const [officeId, setOfficeId] = useState(null);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState(200);

  const [createOpen, setCreateOpen] = useState(false);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [pwUser, setPwUser] = useState(null);

  useEffect(() => {
    getAdministeredOffices().then((r) => {
      if (ok(r) && Array.isArray(r.json)) {
        setOffices(r.json);
        if (r.json.length) setOfficeId(r.json[0].id);
        else setStatus(r.status);
      } else {
        setStatus(r.status || 0);
      }
    });
  }, []);

  const load = useCallback(async () => {
    if (!officeId) return;
    setLoading(true);
    try {
      const r = await getOfficeUsers(officeId);
      setStatus(r.status);
      setRows(ok(r) && Array.isArray(r.json) ? r.json : []);
    } finally {
      setLoading(false);
    }
  }, [officeId]);

  useEffect(() => {
    load();
  }, [load]);

  const remove = async (row) => {
    const r = await removeOfficeUser(officeId, row.id);
    if (ok(r)) {
      message.success(`${row.email} removed from this office.`);
      load();
    } else {
      message.error(err(r, "Couldn't remove the user."));
    }
  };

  const columns = useMemo(
    () => [
      {
        title: "Team member",
        render: (_, r) => (
          <div className="crm-cell-practice">
            <span className="crm-avatar-sm office"><TeamOutlined /></span>
            <div className="crm-cell-stack">
              <div className="crm-name">{r.name || r.email}</div>
              <div className="crm-sub">{r.email}</div>
            </div>
          </div>
        ),
      },
      {
        title: "Status",
        width: 120,
        align: "center",
        render: (_, r) => (
          <span className={`crm-status-pill ${r.is_active ? "on" : "off"}`}>
            {r.is_active ? "Active" : "Inactive"}
          </span>
        ),
      },
      {
        title: "Verified",
        width: 100,
        align: "center",
        render: (_, r) =>
          r.email_verified ? (
            <Tooltip title="Can log in"><CheckCircleTwoTone twoToneColor="#52c41a" /></Tooltip>
          ) : (
            <Tooltip title="Invited — hasn't finished signup"><ClockCircleOutlined style={{ color: "#faad14" }} /></Tooltip>
          ),
      },
      {
        title: "Access",
        width: 120,
        render: (_, r) => (r.removable ? <Tag>Member</Tag> : <Tag color="gold">Owner</Tag>),
      },
      {
        title: "",
        key: "actions",
        width: 240,
        align: "right",
        render: (_, r) => (
          <div className="crm-row-actions">
            <Button size="small" icon={<KeyOutlined />} onClick={() => setPwUser(r)}>
              Password
            </Button>
            {r.removable ? (
              <Popconfirm
                title="Remove from this office?"
                description="They lose access to this office. Their account stays."
                okText="Remove"
                okButtonProps={{ danger: true }}
                onConfirm={() => remove(r)}
              >
                <Button size="small" danger icon={<UserDeleteOutlined />}>Remove</Button>
              </Popconfirm>
            ) : (
              <Tooltip title="Owner access can't be removed here">
                <Button size="small" icon={<UserDeleteOutlined />} disabled>Remove</Button>
              </Tooltip>
            )}
          </div>
        ),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [officeId]
  );

  const header = (
    <div className="crm-page-head">
      <div className="crm-page-head-left">
        <span className="crm-page-icon"><TeamOutlined /></span>
        <div>
          <div className="crm-page-title">User Management</div>
          <div className="crm-page-sub">Manage the team for each of your offices</div>
        </div>
      </div>
      <div className="crm-page-actions">
        <Select
          style={{ minWidth: 240 }}
          placeholder="Select an office"
          value={officeId}
          onChange={setOfficeId}
          options={offices.map((o) => ({
            value: o.id,
            label: o.practice ? `${o.name} — ${o.practice}` : o.name,
          }))}
        />
        <Button icon={<MailOutlined />} disabled={!officeId} onClick={() => setInviteOpen(true)}>
          Invite
        </Button>
        <Button icon={<PlusOutlined />} type="primary" disabled={!officeId} onClick={() => setCreateOpen(true)}>
          Add user
        </Button>
        <Button icon={<ReloadOutlined />} onClick={load} loading={loading}>
          Refresh
        </Button>
      </div>
    </div>
  );

  if (status === 403 || (offices.length === 0 && status && status !== 200)) {
    return (
      <DashboardLayout>
        <div className="crm-page">
          {header}
          <div className="crm-table-card crm-403">
            <Empty description="You don't manage any offices." />
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="crm-page">
        {header}
        <div className="crm-table-card">
          <Table
            rowKey="id"
            columns={columns}
            dataSource={rows}
            loading={loading}
            locale={{ emptyText: <Empty description="No users in this office yet." /> }}
            pagination={{ pageSize: 25, showSizeChanger: true, pageSizeOptions: ["10", "25", "50", "100"] }}
          />
        </div>

        <CreateUserModal
          open={createOpen}
          officeId={officeId}
          onClose={() => setCreateOpen(false)}
          onDone={() => { setCreateOpen(false); load(); }}
        />
        <InviteUserModal
          open={inviteOpen}
          officeId={officeId}
          onClose={() => setInviteOpen(false)}
          onDone={() => setInviteOpen(false)}
        />
        <SetPasswordModal
          officeId={officeId}
          user={pwUser}
          onClose={() => setPwUser(null)}
          onDone={() => setPwUser(null)}
        />
      </div>
    </DashboardLayout>
  );
}

function CreateUserModal({ open, officeId, onClose, onDone }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) { setEmail(""); setPassword(""); } }, [open]);

  const submit = async () => {
    if (!email.trim() || !password) {
      message.error("Email and password are required.");
      return;
    }
    setBusy(true);
    const r = await createOfficeUser(officeId, email.trim(), password);
    setBusy(false);
    if (ok(r)) {
      message.success("User created — they can log in now.");
      onDone();
    } else {
      message.error(err(r, "Could not create the user."));
    }
  };

  return (
    <Modal title="Add user to office" open={open} onCancel={onClose} onOk={submit}
      okText="Create" confirmLoading={busy} zIndex={10050} destroyOnClose>
      <p className="crm-sub">Creates a login-ready account with full access to this office.</p>
      <label className="crm-field-label">Email</label>
      <Input placeholder="person@example.com" value={email} onChange={(e) => setEmail(e.target.value)} />
      <label className="crm-field-label" style={{ marginTop: 12 }}>Temporary password</label>
      <Input.Password autoComplete="new-password" placeholder="Set an initial password" value={password} onChange={(e) => setPassword(e.target.value)} />
    </Modal>
  );
}

function InviteUserModal({ open, officeId, onClose, onDone }) {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) setEmail(""); }, [open]);

  const submit = async () => {
    if (!email.trim()) {
      message.error("Email is required.");
      return;
    }
    setBusy(true);
    const r = await inviteOfficeUser(officeId, email.trim());
    setBusy(false);
    if (ok(r)) {
      message.success(r.json && r.json.email_sent ? "Invite sent." : "Invite created (email couldn't be sent).");
      onDone();
    } else {
      message.error(err(r, "Could not send the invite."));
    }
  };

  return (
    <Modal title="Invite user to office" open={open} onCancel={onClose} onOk={submit}
      okText="Send invite" confirmLoading={busy} zIndex={10050} destroyOnClose>
      <p className="crm-sub">Sends a signup link; the invitee sets their own password and joins this office.</p>
      <label className="crm-field-label">Email</label>
      <Input placeholder="person@example.com" value={email} onChange={(e) => setEmail(e.target.value)} />
    </Modal>
  );
}

function SetPasswordModal({ officeId, user, onClose, onDone }) {
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { setPassword(""); }, [user]);

  const submit = async () => {
    if (!password) {
      message.error("Enter a new password.");
      return;
    }
    setBusy(true);
    const r = await setOfficeUserPassword(officeId, user.id, password);
    setBusy(false);
    if (ok(r)) {
      message.success("Password updated.");
      onDone();
    } else {
      message.error(err(r, "Could not update the password."));
    }
  };

  return (
    <Modal title={user ? `Set password — ${user.email}` : "Set password"} open={!!user}
      onCancel={onClose} onOk={submit} okText="Update password" confirmLoading={busy}
      zIndex={10050} destroyOnClose>
      <label className="crm-field-label">New password</label>
      <Input.Password autoComplete="new-password" placeholder="New password" value={password} onChange={(e) => setPassword(e.target.value)} />
    </Modal>
  );
}
